import { useCallback, useEffect, useState } from "react";
import { FormattedMessage, injectIntl, WrappedComponentProps } from "react-intl";
import { connect, ConnectedProps } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import ConfirmModal from "../../../../../components/confirm-modal";
import ErrorMessage from "../../../../../components/error-message";
import UpInput from "../../../../../components/up-input";
import UpInputWithLabel from "../../../../../components/UpInputWithLabel";
import { RootReduxState } from "../../../../../utils/types";
import {
  ShopwerkRequestReduxState,
  getShopwerkIntegrationSettingsAction,
  resetShopwerkIntegrationsRequestsAction,
  setupShopwerkIntegrationAction
} from "./redux";

type ShopwerkConnectModalComponentOwnProps = {
  handleClose: () => void;
};

const mapStateToProps = (state: RootReduxState) => {
  const selectedShopId = state.getIn([
    "customerBackendDashboard",
    "userData",
    "selectedShopId"
  ]) as string;

  const setupRequestState = state
    .getIn(["integrations", "shopwerk", "requests", "setup"])
    .toJS() as ShopwerkRequestReduxState;

  return {
    shopId: Number(selectedShopId),
    setupRequestState
  };
};
const matchDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      getShopwerkIntegrationSettingsAction,
      setupShopwerkIntegrationAction,
      resetShopwerkIntegrationsRequestsAction
    },
    dispatch
  );
const connector = connect(mapStateToProps, matchDispatchToProps);

type ShopwerkConnectModalComponentProps = ShopwerkConnectModalComponentOwnProps &
  ConnectedProps<typeof connector>;

const ShopwerkConnectModalComponent = (
  props: ShopwerkConnectModalComponentProps & WrappedComponentProps
) => {
  const {
    handleClose,
    intl,
    setupShopwerkIntegrationAction,
    getShopwerkIntegrationSettingsAction,
    resetShopwerkIntegrationsRequestsAction,
    shopId,
    setupRequestState
  } = props;

  const [apiKey, setApiKey] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [isSubmittable, setIsSubmittable] = useState(false);

  useEffect(
    () => () => {
      resetShopwerkIntegrationsRequestsAction();
    },
    []
  );

  useEffect(() => {
    if (setupRequestState.success) {
      getShopwerkIntegrationSettingsAction(shopId);
      handleClose();
    }
  }, [setupRequestState.success]);

  useEffect(() => {
    setIsSubmittable(apiKey.trim().length > 0);
  }, [apiKey]);

  const handleSubmit = useCallback(() => {
    setupShopwerkIntegrationAction(shopId, apiKey, endpoint);
  }, [shopId, apiKey, endpoint]);

  const handleApiKeyChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setApiKey(e.target.value);
  }, []);

  const handleEndpointChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEndpoint(e.target.value);
  }, []);

  const getErrorMessage = useCallback(() => {
    switch (setupRequestState.error) {
      case "invalid-key": {
        return intl.formatMessage({
          id: "containers.Integrations.shopwerk.popup.errors.invalidKey",
          defaultMessage:
            'Please verify your API key. If the key is valid but connections are still blocked, go to: Account menu → Security → Authorized IPs → disable "Block unknown IP addresses."'
        });
      }
      case "unknown-error": {
        return intl.formatMessage({
          id: "containers.Integrations.shopwerk.popup.errors.other",
          defaultMessage: "Error connecting Shopwerk"
        });
      }
      default: {
        return null;
      }
    }
  }, [setupRequestState.error, intl]);

  return (
    <ConfirmModal
      title={
        <FormattedMessage
          id="containers.Integrations.shopwerk.popup.headline"
          defaultMessage="Connect Account"
        />
      }
      leftButtonOnClick={handleClose}
      rightButtonOnClick={handleSubmit}
      leftButtonText={
        <FormattedMessage
          id="containers.Integrations.shopwerk.popup.button.cancel"
          defaultMessage="Cancel"
        />
      }
      rightButtonText={
        <FormattedMessage
          id="containers.Integrations.shopwerk.popup.button.connect"
          defaultMessage="Connect"
        />
      }
      rightButtonDisabled={!isSubmittable}
      close={handleClose}
      loading={setupRequestState.loading ?? false}
      visible
    >
      <span style={{ fontWeight: 500 }}>
        <FormattedMessage
          id={`containers.Integrations.shopwerk.popup.description`}
          defaultMessage="Please enter your Shopwerk API Key."
        />
      </span>
      <UpInputWithLabel
        label={intl.formatMessage({
          id: "containers.Integrations.shopwerk.popup.endpoint.label",
          defaultMessage: "Your Shopwerk Endpoint"
        })}
        margin="10px 0 10px 0"
        width="100%"
        flex="1 1"
      >
        <UpInput
          value={endpoint}
          onChange={handleEndpointChange}
          width="100%"
          type="text"
          placeholder={intl.formatMessage({
            id: "containers.Integrations.shopwerk.popup.endpoint.placeholder",
            defaultMessage: "Endpoint"
          })}
        />
      </UpInputWithLabel>
      <UpInputWithLabel
        label={intl.formatMessage({
          id: "containers.Integrations.shopwerk.popup.input.label",
          defaultMessage: "Your Shopwerk API Key"
        })}
        margin="10px 0 10px 0"
        width="100%"
        flex="1 1"
      >
        <UpInput
          value={apiKey}
          onChange={handleApiKeyChange}
          width="100%"
          type="text"
          placeholder={intl.formatMessage({
            id: "containers.Integrations.brevo.popup.input.placeholder",
            defaultMessage: "API Key"
          })}
        />
      </UpInputWithLabel>
      <ErrorMessage>{getErrorMessage()}</ErrorMessage>
    </ConfirmModal>
  );
};

export const ShopwerkConnectModal = connector(injectIntl(ShopwerkConnectModalComponent));
